import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DeleteColumnValidationSchema,
  type TDeleteColumnValidation,
  useDeleteColumnMutation,
} from '../../api/delete-column.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_COLUMN_QUERY_KEY } from '../../api/get-list-column.api';
import { TOAST_MESSAGES_DELETE_COLUMN } from './delete-column-modal.constant';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

export interface IUseDeleteColumnModalProps {
  spaceId: string;
  spaceColumnId: string;
}

export function useDeleteColumnModal({ spaceId, spaceColumnId }: IUseDeleteColumnModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TDeleteColumnValidation>({
    resolver: zodResolver(DeleteColumnValidationSchema),
    mode: 'onChange',
  });

  const mutationDeleteColumn = useDeleteColumnMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_COLUMN.DELETE_COLUMN_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([
          // queryClient.invalidateQueries({
          //   queryKey: [GET_SINGLE_SPACE_QUERY_KEY, { spaceId }],
          // }),
          // queryClient.invalidateQueries({
          //   queryKey: [GET_LIST_TICKET_BY_SPACE_QUERY_KEY, { spaceId }],
          // }),
          queryClient.invalidateQueries({
            queryKey: [
              GET_LIST_COLUMN_QUERY_KEY,
              {
                spaceId: spaceId,
                search: undefined,
                page: undefined,
                limit: undefined,
                sortDir: undefined,
                sortBy: undefined,
              },
            ],
          }),
        ]);
        modalActions.closeModal('DeleteColumnModal');
        showToast({
          message: TOAST_MESSAGES_DELETE_COLUMN.DELETE_COLUMN_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenDeleteTicketingColumnModal = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteColumnModal');
  }, [modalActions]);

  // EFFECTS
  useEffect(() => {
    if (spaceColumnId) {
      form.reset({
        spaceColumnId: spaceColumnId || '',
      });
    }
  }, [spaceColumnId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('DeleteColumnModal');
  }, [modalActions]);

  const handleSubmitForm = useCallback(
    (data: TDeleteColumnValidation) => {
      mutationDeleteColumn.mutate({
        spaceColumnId: data?.spaceColumnId || '',
      });
    },
    [mutationDeleteColumn]
  );

  return {
    // Form
    form,

    // Modal
    isOpenDeleteTicketingColumnModal,

    // Mutation
    mutationDeleteColumn,

    // Handlers
    handleCloseModal,
    handleSubmitForm,
  };
}
